import LineIcon from "@assets/images/line.svg";
import RefreshIcon from "@assets/images/refresh-primary.svg";
import {
  Box,
  Button,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  GridSize,
  Radio,
  RadioGroup
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { FIELD_TYPE } from "@saleor/changeOrderExport/components/OrderFilterSection/constant";
import OrderStatus from "@saleor/changeOrderExport/components/OrderStatus";
import DatePicker from "@saleor/components/DatePicker";
import { FormSpacer } from "@saleor/components/FormSpacer";
import MultipleAutocompleteTextInput from "@saleor/components/MultipleAutocompleteTextInput";
import MyTypography from "@saleor/components/MyTypography";
import SingleSelectField from "@saleor/components/SingleSelectField";
import { SingleTextField } from "@saleor/components/SingleTextField";
import useOrderFilterConfirmation from "@saleor/hooks/useOrderFilterOrderConfirmation";
import {
  handleChangeCreateDateGte,
  handleChangeCreateDateLte
} from "@saleor/OrderConfirm/utils";
import { mapEdgesToItems } from "@saleor/utils/maps";
import cls from "classnames";
import moment from "moment";
import React, { useEffect, useMemo } from "react";
import SVG from "react-inlinesvg";
import { FormattedMessage } from "react-intl";

import useStyles from "./styles";

export interface IOrderListFilterProps {
  handleChangeFilter?: (filterObj: any) => void;
  loading?: boolean;
  isSearched: boolean;
  message: JSX.Element | string;
  listOrder: any[];
}

const OrderFilterSectionAdmin: React.FC<IOrderListFilterProps> = ({
  handleChangeFilter,
  message,
  loading,
  isSearched,
  listOrder
}) => {
  const classes = useStyles();
  const {
    formik,
    // setDataFormik,
    isOpen,
    setIsOpen,
    isClean,
    handleCleanForm,
    formData,
    ArrowIconComponent,
    getMaterialAdminOrder,
    fetchMaterialAdminOrder,
    loadingMaterialAdminOrder,
    fetchSoldToDataSuggestions,
    getSoldToDataSuggestions,
    loadingSoldToDataSuggestion
  } = useOrderFilterConfirmation({ handleChangeFilter });

  useEffect(() => {
    if (listOrder?.length && listOrder?.length > 0 && !loading) {
      setIsOpen(false);
    }
  }, [listOrder, loading]);

  const getTextError = field => (
    <div className={classes.textError}>{formik.errors[field]}</div>
  );

  const isDisabled = useMemo(() => {
    if (
      !formik?.values?.saleOrganization &&
      !formik.values.poNo &&
      !formik.values.soNo
    ) {
      return true;
    }

    return !(
      formik.values.poNo ||
      formik.values.soNo ||
      (formik.values?.orderCreateDate?.gte &&
        formik?.values?.orderCreateDate?.lte)
    );
  }, [
    formik?.values?.saleOrganization,
    formik?.values?.poNo,
    formik?.values?.soNo,
    formik?.values?.orderCreateDate?.gte,
    formik?.values?.orderCreateDate?.lte,
    formik.dirty
  ]);

  // const [] = useFilterSoldToDomesticOrderLazyQuery();
  const mappingDataSuggest = name => {
    if (name === "material") {
      const filterSoldToDomesticOrder =
        fetchMaterialAdminOrder?.filterRequireAttentionMaterial;
      return {
        searchField: "material",
        getDataSuggestions: getMaterialAdminOrder,
        loadingDataSuggestion: loadingMaterialAdminOrder,
        isCheckingRole: true,
        dataOptions:
          mapEdgesToItems(filterSoldToDomesticOrder)?.map(data => ({
            title: `${data.code} - ${data.descriptionEn}`,
            value: data.code
          })) || []
      };
    }
    if (name === "soldTo") {
      const filterSoldToDomesticOrder =
        fetchSoldToDataSuggestions?.searchSuggestionDomesticSoldTos;
      return {
        totalCount: filterSoldToDomesticOrder?.totalCount,
        pageInfo: filterSoldToDomesticOrder?.pageInfo,
        showLoadMore: true,
        fetchDataSuggestions: fetchSoldToDataSuggestions,
        loadingDataSuggestion: loadingSoldToDataSuggestion,
        getDataSuggestions: getSoldToDataSuggestions,
        searchField: "soldTo",
        dataOptions:
          mapEdgesToItems(filterSoldToDomesticOrder)?.map(data => ({
            title: `${data.code} - ${data.name}`,
            value: data.code
          })) || []
      };
    }
  };

  const createdDateRange = useMemo(() => {
    const { soldTo = [], material = [] } = formik.values ?? {};
    const soldToCount = soldTo.length;
    const materialCount = material.length;

    if (soldToCount === 1 || (soldToCount === 0 && materialCount === 1)) {
      return 91;
    }
    return 31;
  }, [formik.values.soldTo, formik.values.material]);

  useEffect(() => {
    const { gte, lte } = formik.values.orderCreateDate ?? {};
    const maxDiff = createdDateRange - 1;

    if (gte && lte) {
      const diffDays = moment(lte).diff(moment(gte), "days");
      if (Math.abs(diffDays) > maxDiff) {
        formik.setFieldValue("orderCreateDate", { gte: "", lte: "" });
      }
    }
  }, [createdDateRange]);

  const getGridSize = (value, type, size) => {
    if (value) {
      return value as GridSize;
    } else if (size === "md") {
      return type === FIELD_TYPE.DATEPICKER ||
        type === FIELD_TYPE.SUGGESTION_INPUT
        ? 6
        : 3;
    } else {
      return type === FIELD_TYPE.DATEPICKER ||
        type === FIELD_TYPE.SUGGESTION_INPUT
        ? 12
        : 6;
    }
  };

  const renderItem = (
    <Grid className={classes.searchBox} container spacing={3}>
      {formData.map((child, key) => {
        const renderChild = (children: JSX.Element) => (
          <Grid
            item
            md={getGridSize(child.md, child.type, "md")}
            sm={getGridSize(child.sm, child.type, "sm")}
            key={key}
            className={cls({
              [classes.gridDatePicker]: child.type === FIELD_TYPE.DATEPICKER
            })}
          >
            <div
              className={cls({
                [classes.boxDatePicker]: child.type === FIELD_TYPE.DATEPICKER
              })}
            >
              <MyTypography variant={"subtitle3"}>
                {child.title}
                {child.required ? (
                  <span className={classes.required}>*</span>
                ) : (
                  ""
                )}
                {child.subTitle ? (
                  <span
                    className={classes.subTitle}
                  >{` ${child.subTitle}`}</span>
                ) : (
                  ""
                )}
              </MyTypography>
              {children}
              {child.type !== FIELD_TYPE.DATEPICKER && child.name !== "soldTo"
                ? getTextError(child.name)
                : ""}
            </div>
          </Grid>
        );
        if (child.type === FIELD_TYPE.MULTIPLE_SELECT) {
          return renderChild(
            <OrderStatus
              disableIndeterminate
              showChipDeleteButton={false}
              choices={child.options}
              name={child.name}
              id={child.name}
              testId={child.name}
              value={formik.values[child.name]}
              onChange={formik.handleChange}
              showCircularIcon={false}
              isClean={isClean}
              placeholder={child.placeholder}
              listItemClass={classes.listItemClass}
              numberItemDisplay={2}
              error={!!formik.errors[child.name]}
            />
          );
        }
        if (child.type === FIELD_TYPE.SINGLE_DATE) {
          return renderChild(
            <DatePicker
              name={`${child.name}`}
              id={child.name}
              data-testid={child.name}
              disableManualInput
              value={formik.values[child.name]}
              onChange={formik.handleChange}
              format="YYYY-MM-DD"
            />
          );
        }
        if (child.type === FIELD_TYPE.SINGLE_INPUT) {
          return renderChild(
            <SingleTextField
              name={child.name}
              id={child.name}
              testId={child.name}
              value={formik.values[child.name]}
              onChange={formik.handleChange}
              placeholder={child.placeholder}
              disabled={child.disabled}
              error={!!formik.errors[child.name]}
              maxLengthProps={child?.maxLength ? child.maxLength : undefined}
            />
          );
        }
        if (child.type === FIELD_TYPE.SINGLE_SELECT) {
          return renderChild(
            <SingleSelectField
              className={classes.selectField}
              menuListCustomStyle={{
                overflowY: "auto"
              }}
              choices={child.options}
              name={child.name}
              id={child.name}
              testId={child.name}
              InputProps={{
                inputProps: { "data-testid": `${child.name}-input` },
                placeholder: child.placeholder
              }}
              onChange={formik.handleChange}
              value={formik.values[child.name]}
              placeholder={child.placeholder}
              error={!!formik.errors[child.name]}
              disabled={child?.disabled}
            />
          );
        }

        if (child.type === FIELD_TYPE.SUGGESTION_INPUT) {
          const dataSuggest = mappingDataSuggest(child.name);
          if (child.name === "soldTo") {
            return renderChild(
              <MultipleAutocompleteTextInput
                showCount
                maxCount={10}
                reRender={isClean}
                fullWidthItem
                name={child.name}
                id={child.name}
                testId={child.name}
                value={formik.values.soldTo}
                keyFilterApi={"search"}
                headerText={"Sold-to ที่เลือกค้นหา"}
                getDataSuggestions={result => {
                  dataSuggest.getDataSuggestions({
                    ...result,
                    variables: {
                      distributionChannelType:
                        result.variables.distributionChannelType,
                      filter: result.variables.filter,
                      first: result.variables.first,
                      bu: formik.values.bu
                    }
                  });
                }}
                fetchDataSuggestions={dataSuggest.fetchDataSuggestions}
                loadingDataSuggestion={dataSuggest.loadingDataSuggestion}
                dataOptions={dataSuggest.dataOptions}
                message={"ยังไม่มีข้อมูลที่เลือกค้นหา"}
                placeholder={"ค้นหา Sold-to"}
                handleSelectedSearch={value => {
                  formik.handleChange({
                    target: {
                      name: "soldTo",
                      value
                    }
                  });
                }}
                // error={!!formik.errors[child.name]}
                // textError={getTextError(child.name)}
              />
            );
          } else if (child.name === "material") {
            const dataSuggestMaterial = mappingDataSuggest(child.name);
            return renderChild(
              <MultipleAutocompleteTextInput
                // showCount
                reRender={isClean}
                maxCount={1}
                fullWidthItem
                name={child.name}
                id={child.name}
                testId={child.name}
                getByValue
                keyFilterApi={"material"}
                isCheckingRole
                headerText={"Material Code / Description ที่เลือกค้นหา"}
                getDataSuggestions={dataSuggestMaterial.getDataSuggestions}
                fetchDataSuggestions={dataSuggestMaterial.fetchDataSuggestions}
                loadingDataSuggestion={
                  dataSuggestMaterial.loadingDataSuggestion
                }
                dataOptions={dataSuggestMaterial.dataOptions}
                message={"ยังไม่มีข้อมูลที่เลือกค้นหา"}
                placeholder={"ค้นหา Material Code / Description"}
                handleSelectedSearch={value => {
                  formik.handleChange({
                    target: {
                      name: "material",
                      value
                    }
                  });
                }}
              />
            );
          }
        }

        if (child.type === FIELD_TYPE.RADIO_GROUP) {
          return renderChild(
            <RadioGroup
              className={classes.radioGroup}
              aria-labelledby="demo-controlled-radio-buttons-group"
              name={child.name}
              id={child.name}
              data-testid={child.name}
              value={formik.values[child.name]}
              onChange={formik.handleChange}
            >
              {child.options?.map((o, oKey) => (
                <FormControlLabel
                  key={`radio${oKey}`}
                  id={`radio${oKey}`}
                  data-testid={`radio${oKey}`}
                  value={o.value}
                  control={<Radio />}
                  label={
                    <MyTypography variant={"body3"}>{o.label}</MyTypography>
                  }
                />
              ))}
            </RadioGroup>
          );
        }
        if (child.type === FIELD_TYPE.DATEPICKER) {
          const maxPeriod = createdDateRange - 1;
          return renderChild(
            <Box display={"flex"} justifyItems="center" alignItems={"start"}>
              <div className={classes.itemDatePicker}>
                <DatePicker
                  type="gte"
                  dateRange={createdDateRange}
                  error={!!formik.errors[child.name]?.gte}
                  name={`${child.name}_gte`}
                  id={`${child.name}_gte`}
                  data-testid={`${child.name}_gte`}
                  disableManualInput
                  value={formik.values[child.name]?.gte || ""}
                  onChange={e =>
                    handleChangeCreateDateGte({
                      formik,
                      event: e,
                      maxPeriod,
                      child
                    })
                  }
                  format="YYYY-MM-DD"
                  maxDate={
                    formik.values[child.name].lte
                      ? moment(formik.values[child.name].lte)
                      : undefined
                  }
                  minDate={undefined}
                  noBlock
                />
                <div className={classes.textError}>
                  {formik.errors.orderCreateDate?.gte}
                </div>
              </div>
              <Box px={1} className={classes.boxLine}>
                <SVG src={LineIcon} width={12} height={2} />
              </Box>
              <div className={classes.itemDatePicker}>
                <DatePicker
                  type="lte"
                  dateRange={createdDateRange}
                  error={!!formik.errors[child.name]?.lte}
                  name={`${child.name}_lte`}
                  id={`${child.name}_lte`}
                  data-testid={`${child.name}_lte`}
                  disableManualInput
                  value={formik.values[child.name]?.lte || ""}
                  onChange={e => {
                    handleChangeCreateDateLte({
                      formik,
                      event: e,
                      maxPeriod,
                      child
                    });
                  }}
                  format="YYYY-MM-DD"
                  minDate={
                    formik.values[child.name].gte
                      ? moment(formik.values[child.name].gte)
                      : undefined
                  }
                  maxDate={undefined}
                  noBlock
                />
                <div className={classes.textError}>
                  {formik.errors.orderCreateDate?.lte}
                </div>
              </div>
            </Box>
          );
        }
      })}
    </Grid>
  );

  return (
    <>
      <div className={classes.wrapper}>
        <Box className={classes.root}>
          <Grid
            className={classes.header}
            container
            onClick={() => setIsOpen(!isOpen)}
          >
            <MyTypography variant="h6">
              <FormattedMessage defaultMessage="ค้นหา Order Confirmation" />
            </MyTypography>
            <ArrowIconComponent className={classes.arrowIcon} />
          </Grid>
          <Collapse in={isOpen}>
            {renderItem}
            <Box className={classes.divider}>
              <FormSpacer />
              <Divider />
            </Box>
            <Box className={classes.bottomBar} ml={3}>
              <Box>
                {formik.errors.soldTo ? getTextError("soldTo") : message}
              </Box>
              <Box className={classes.flex}>
                <Box mr={2}>
                  <Button
                    className={classes.resetButton}
                    variant={"outlined"}
                    color={"secondary"}
                    disabled={!formik.dirty && !isSearched}
                    onClick={handleCleanForm}
                    name="resetButton"
                    id="resetButton"
                  >
                    <SVG
                      src={RefreshIcon}
                      className={classes.icon}
                      width={16}
                      height={16}
                    />
                    <FormattedMessage defaultMessage="ล้างข้อมูล" />
                  </Button>
                </Box>
                <Box>
                  <Button
                    className={classes.actionButton}
                    variant={"contained"}
                    disabled={isDisabled || loading}
                    color={"secondary"}
                    onClick={formik.submitForm}
                    name="submitButton"
                    id="submitButton"
                  >
                    <SearchIcon style={{ fontSize: "18px" }} />
                    <FormattedMessage defaultMessage="ค้นหา" />
                  </Button>
                </Box>
              </Box>
            </Box>
          </Collapse>
        </Box>
      </div>
    </>
  );
};

export default OrderFilterSectionAdmin;
